import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FaFacebookF, FaWhatsapp, FaTiktok, FaInstagram } from "react-icons/fa6";
import { useLang } from "../Utility/Languagecontext";
import { scrollToEl, scrollToTop } from "../Utility/scroll";
import { COMPANY, whatsappUrl } from "../Data/Company";
// نفس روابط النافبار بالظبط (مصدر واحد للروابط)
import { LINKS, CTA_TO } from "./Navbarefixed";

const ICONS = { facebook: FaFacebookF, instagram: FaInstagram, tiktok: FaTiktok };
const SOCIALS = [
  ...COMPANY.socials.map((s) => ({ ...s, Icon: ICONS[s.id] })),
  { id: "whatsapp", label: "WhatsApp", href: whatsappUrl(), Icon: FaWhatsapp },
];

const EASE = [0.76, 0, 0.24, 1];
// روابط الفوتر = روابط النافبار + دليل الشراء
const FOOT_LINKS = [...LINKS, { id: "guide", to: "/off-plan-guide" }];

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);

export const Footer = () => {
  const { t, lang, locale, languages, hrefFor } = useLang();
  const f = t.footer;
  const c = t.contact.info;
  const { pathname, hash } = useLocation();
  const year = new Date().getFullYear().toLocaleString(locale, { useGrouping: false });

  const toTop = scrollToTop;

  const onNav = (e, to) => {
    const [path, targetHash] = to.split("#");
    const samePath = pathname === (path || "/");
    const sameHash = targetHash ? hash === `#${targetHash}` : !hash;
    if (samePath && sameHash) {
      e.preventDefault();
      const el = targetHash ? document.getElementById(targetHash) : null;
      if (el) scrollToEl(el);
      else scrollToTop();
    }
  };

  return (
    <footer className="foot">
      <div className="foot__main">
        {/* ====== البراند + السوشيال ====== */}
        <motion.div
          className="foot__brand"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <Link to="/#home" className="foot__name" onClick={(e) => onNav(e, "/#home")}>
            {t.brand}
          </Link>
          <p className="foot__tag">{f.tagline}</p>

          <p className="foot__follow">{f.follow}</p>
          <ul className="foot__social">
            {SOCIALS.map(({ id, label, href, Icon }) => (
              <li key={id}>
                <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="sbtn">
                  <Icon aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* ====== الروابط ====== */}
        <motion.nav
          className="foot__col"
          aria-label={f.explore}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, delay: 0.1, ease: EASE }}
        >
          <h3 className="foot__head">{f.explore}</h3>
          <ul>
            {FOOT_LINKS.map((k) => (
              <li key={k.id}>
                <Link to={k.to} className="flink" onClick={(e) => onNav(e, k.to)}>
                  {t.nav.links[k.id]}
                </Link>
              </li>
            ))}
          </ul>
        </motion.nav>

        {/* ====== التواصل ====== */}
        <motion.div
          className="foot__col"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, delay: 0.2, ease: EASE }}
        >
          <h3 className="foot__head">{f.contact}</h3>
          <address className="foot__address">
            <ul>
              <li>
                <a href={`tel:${COMPANY.phone}`} className="flink is-ltr">
                  {COMPANY.phoneLabel}
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} className="flink is-ltr">
                  {COMPANY.email}
                </a>
              </li>
              <li>
                <a href={COMPANY.mapShareUrl} target="_blank" rel="noopener noreferrer" className="foot__addr">
                  {c.addressValue}
                </a>
              </li>
              <li className="foot__addr">
                {c.hoursValue}
                <br />
                {c.closed}
              </li>
            </ul>
          </address>
          <Link to={CTA_TO} className="btn btn--ghost foot__cta" onClick={(e) => onNav(e, CTA_TO)}>
            {t.nav.cta}
          </Link>
        </motion.div>
      </div>

      {/* ====== اللغات (روابط لنفس الصفحة بكل لغة) ====== */}
      <nav className="foot__langs" aria-label={f.languages}>
        {languages.map((l) => (
          <a
            key={l.code}
            href={hrefFor(l.code, pathname)}
            hrefLang={l.hreflang || l.code}
            lang={l.code}
            className={l.code === lang ? "is-on" : ""}
            aria-current={l.code === lang ? "true" : undefined}
          >
            {l.label}
          </a>
        ))}
      </nav>

      {/* ====== الاسم الكبير المفرّغ ====== */}
      <div className="foot__mark" aria-hidden="true">
        <motion.span
          initial={{ y: "70%", opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.4, ease: EASE }}
        >
          {t.brand}
        </motion.span>
      </div>

      {/* ====== الشريط السفلي ====== */}
      <div className="foot__bar">
        <p>
          © {year} {t.brandFull}. {f.rights}
        </p>
        <button type="button" className="foot__top" onClick={toTop}>
          {f.top}
          <span className="foot__top-ic">
            <Arrow />
          </span>
        </button>
      </div>
    </footer>
  );
};
